import { useMemo, useRef, useState } from "react";

import dotmap from "../../data/dotmap.json";
import { href, type Dictionary, type Lang } from "@/content/site";

import { BandLink, LedgerLink } from "./ctas";
import { Link } from "./link";
import { LoopMedia } from "./loop-media";
import { CountUp, Reveal, useReveal } from "./motion";

/* ---------- 1. Proof strip (oversized metrics) ---------- */
export function ProofStrip({ t }: { t: Dictionary }) {
  return (
    <section className="im-section im-section--tight" aria-label={t.a11y.keyFigures}>
      <div className="im-wrap">
        <div className="im-proof">
          {t.proof.map((p) => (
            <Reveal className="im-proof__cell" key={p.label}>
              <div className="im-proof__value">
                <CountUp value={p.value} />
              </div>
              <p className="im-proof__label">{p.label}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- 2. Divisions accordion (hover / click slices) ---------- */
export function DivisionsAccordion({ lang, t }: { lang: Lang; t: Dictionary }) {
  const [open, setOpen] = useState<number | null>(2);
  return (
    <section className="im-section" id="divisions">
      <div className="im-wrap">
        <Reveal>
          <h2 className="im-display-sm max-w-[16ch]">{t.divisionsSection.title}</h2>
          <p className="im-body-text mt-5">{t.divisionsSection.intro}</p>
        </Reveal>
        <div className="im-acc mt-12">
          {t.divisions.map((d, i) => (
            <div className="im-acc__row" data-open={open === i ? "true" : "false"} key={d.slug}>
              <button
                className="im-acc__head"
                type="button"
                aria-expanded={open === i}
                aria-controls={`div-${d.slug}`}
                onClick={() => setOpen((prev) => (prev === i ? null : i))}
                onMouseEnter={() => {
                  if (typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
                    setOpen(i);
                  }
                }}
              >
                <img fetchPriority="high" className="im-acc__icon" src={d.icon} alt="" width={42} height={42}/>
                <span className="im-acc__name">{d.name}</span>
                <span className="im-acc__code">
                  DIV {d.code} // {d.short.split(",")[0]}
                </span>
                <span className="im-acc__plus" aria-hidden="true" />
              </button>
              <div className="im-acc__panel" id={`div-${d.slug}`}>
                <div>
                  <div className="im-acc__body">
                    <div>
                      <p className="im-acc__text">{d.scope}</p>
                      <div className="mt-6">
                        <BandLink to={href(lang, `/divisions/${d.slug}`)} meta={t.divisionsSection.open}>
                          {d.name}
                        </BandLink>
                      </div>
                    </div>
                    <LoopMedia className="im-acc__img" image={d.image} alt={d.name} active={open === i} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const partnerLogos = import.meta.glob("/src/assets/partners/*.{svg,png,webp,jpg}", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

function partnerLogo(key: string) {
  const hit = Object.entries(partnerLogos).find(([path]) => path.split("/").pop()?.replace(/\.[^.]+$/, "") === key);
  return hit?.[1];
}

/* ---------- 2b. Partner logo wall ---------- */
export function Partners({ lang, t }: { lang: Lang; t: Dictionary }) {
  const p = t.partners;
  return (
    <section className="im-section" id="partners">
      <div className="im-wrap">
        <Reveal className="mx-auto max-w-[44rem] text-center">
          <h2 className="im-display-sm">{p.title}</h2>
          <p className="im-body-text mx-auto mt-5">{p.intro}</p>
        </Reveal>
        <div className="im-pwall mt-14">
          {p.items.map((item) => (
            <Reveal className="im-pwall__item" key={item.name}>
              <Link className="im-pw-link" to={href(lang, `/divisions/${item.division}`)}>
                {partnerLogo(item.logoKey) ? (
                  <>
                    <span className="im-pw-logo">
                      <img src={partnerLogo(item.logoKey)} alt={item.name} width={160} height={48} loading="lazy" decoding="async"/>
                    </span>
                    <span className="im-pw-name">{item.name}</span>
                  </>
                ) : (
                  <span className="im-pw-mark">{item.name}</span>
                )}
                <span className="im-pw-sector">{item.sector}</span>
                <p className="im-pw-body">{item.body}</p>
                <span className="im-pw-meta">{item.meta}</span>
              </Link>
            </Reveal>
          ))}
        </div>
        <p className="im-pwall__note">{p.note}</p>
      </div>
    </section>
  );
}

/* ---------- 3. Three layers ledger ---------- */
export function Layers({ t }: { t: Dictionary }) {
  const icons = ["/assets/icons/representation.webp", "/assets/icons/inspection.webp", "/assets/icons/container.webp"];
  return (
    <section className="im-section">
      <div className="im-plate im-plate--steel" aria-hidden="true" />
      <div className="im-wrap im-rel">
        <Reveal className="mx-auto max-w-[44rem] text-center">
          <h2 className="im-display-sm">{t.layers.title}</h2>
          <p className="im-body-text mx-auto mt-5">{t.layers.intro}</p>
        </Reveal>
        <div className="im-layers mt-14">
          {t.layers.items.map((item, i) => (
            <Reveal className="im-layer" key={item.name}>
              <span className="im-layer__n">0{i + 1} / 03</span>
              <div className="im-layer__rule" />
              <img fetchPriority="high" src={icons[i]} alt="" width={44} height={44}/>
              <h3 className="im-h3">{item.name}</h3>
              <p className="im-acc__text mt-3">{item.body}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- 3b. Dual engine ---------- */
export function Engines({ lang, t }: { lang: Lang; t: Dictionary }) {
  const icons = ["/assets/icons/representation.webp", "/assets/icons/commodities.webp"];
  return (
    <section className="im-section" id="dual-engine">
      <div className="im-wrap im-rel">
        <Reveal className="mx-auto max-w-[44rem] text-center">
          <h2 className="im-display-sm">{t.engines.title}</h2>
          <p className="im-body-text mx-auto mt-5">{t.engines.intro}</p>
        </Reveal>
        <div className="im-layers mt-14">
          {t.engines.items.map((item, i) => (
            <Reveal className="im-layer" key={item.name}>
              <span className="im-layer__n">{item.tag}</span>
              <div className="im-layer__rule" />
              <img fetchPriority="high" src={icons[i]} alt="" width={44} height={44}/>
              <h3 className="im-h3">{item.name}</h3>
              <p className="im-acc__text mt-3">{item.body}</p>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10 text-center">
          <Link className="im-route-link" to={href(lang, "/beyond-sourcing")}>
            <span>{t.advantage.title}</span>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- 4. Consolidation route diagram (the signature) ---------- */
export function Consolidation({ t }: { t: Dictionary }) {
  const ref = useReveal<HTMLDivElement>("-20% 0px", "drawn");
  const c = t.consolidation;
  const factories = [
    { y: 70, label: c.nodes.konya },
    { y: 130, label: c.nodes.konya },
    { y: 190, label: c.nodes.konya },
    { y: 330, label: c.nodes.ankara },
    { y: 390, label: c.nodes.istanbul },
  ];
  const ports = [
    { y: 110, label: c.ports.mersin },
    { y: 230, label: c.ports.izmir },
    { y: 350, label: c.ports.ambarli },
  ];
  return (
    <section className="im-section" id="consolidation">
      <div className="im-plate im-plate--container" aria-hidden="true" />
      <div className="im-wrap im-rel im-consol">
        <Reveal axis="x">
          <p className="im-eyebrow">{c.eyebrow}</p>
          <h2 className="im-display-sm mt-4">{c.title}</h2>
          <p className="im-body-text mt-5">{c.body}</p>
          <div className="im-manifest">
            <div className="im-manifest__title">{c.manifestTitle}</div>
            <dl>
              {c.manifest.map((m) => (
                <div key={m.label}>
                  <dt>{m.label}</dt>
                  <dd>{m.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Reveal>
        <div className="im-diagram" ref={ref}>
          <svg viewBox="0 0 900 460" role="img" aria-label={t.a11y.routeDiagram}>
            {[100, 200, 300, 400].map((y) => (
              <line className="d-grid" key={y} x1="0" x2="900" y1={y} y2={y} />
            ))}
            {/* cluster bracket */}
            <rect x="30" y="45" width="150" height="235" fill="none" stroke="currentColor" strokeOpacity="0.15" strokeDasharray="4 4" />
            <text className="d-label" x="40" y="35">
              {c.nodes.cluster}
            </text>
            {factories.map((f, i) => (
              <g key={i}>
                <rect className="d-box" x="60" y={f.y - 12} width="24" height="24" />
                <path className="d-box" d={`M60 ${f.y - 12} l12 -9 l12 9`} />
                <circle className="d-node" cx="100" cy={f.y} r="2.5" />
                <text className="d-label" x="112" y={f.y + 4}>
                  {f.label}
                </text>
                <path className="d-route" d={`M100 ${f.y} C 260 ${f.y}, 300 230, 400 230`} />
              </g>
            ))}
            {/* container */}
            <g transform="translate(400 190)">
              <rect className="d-box" x="0" y="0" width="140" height="80" />
              {[20, 40, 60, 80, 100, 120].map((x) => (
                <line key={x} x1={x} x2={x} y1="0" y2="80" stroke="currentColor" strokeOpacity="0.15" />
              ))}
              <rect className="d-band" x="0" y="20" width="140" height="6" />
              <circle className="d-pulse" cx="70" cy="56" r="4" />
              <text className="d-label d-label--ink" x="0" y="100">
                1 x 40&apos; HC
              </text>
            </g>
            {ports.map((p, i) => (
              <g key={i}>
                <path className="d-route d-route--out" d={`M540 230 C 640 230, 660 ${p.y}, 740 ${p.y}`} />
                <circle className="d-node d-node--seal" cx="740" cy={p.y} r="3" />
                <text className="d-label d-label--ink" x="752" y={p.y - 4}>
                  {p.label}
                </text>
                <text className="d-label" x="752" y={p.y + 12} fontSize="9">
                  {Object.values(c.portsNote)[i]}
                </text>
              </g>
            ))}
            <path className="d-route d-route--dotted" d="M540 235 C 620 300, 680 420, 760 430" />
            <circle className="d-node d-node--seal" cx="760" cy="430" r="3" />
            <text className="d-label d-label--ink" x="772" y="434">
              {c.africa}
            </text>
          </svg>
          <dl className="im-diagram__foot">
            {c.foot.map((f) => (
              <div key={f.label}>
                <dt>{f.label}</dt>
                <dd>{f.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}

/* ---------- 5. Markets dot map ---------- */
type Marker = { key: string; slug: string; label: string; dx: number; dy: number; anchor?: "end" };

export function Markets({ lang, t, asPage = false }: { lang: Lang; t: Dictionary; asPage?: boolean }) {
  // On /markets this section is the page header, so its title is the h1.
  const Title = asPage ? "h1" : "h2";
  const markers = dotmap.markers as unknown as Record<string, [number, number]>;
  const nameOf = (slug: string) => t.markets.find((m) => m.slug === slug)?.name ?? slug;
  const active: Marker[] = [
    { key: "algeria", slug: "algeria", label: nameOf("algeria"), dx: -12, dy: -14, anchor: "end" },
    { key: "tunisia", slug: "tunisia", label: nameOf("tunisia"), dx: 12, dy: -10 },
    { key: "morocco", slug: "morocco", label: nameOf("morocco"), dx: -12, dy: 4, anchor: "end" },
    { key: "senegal", slug: "senegal", label: nameOf("senegal"), dx: -12, dy: 4, anchor: "end" },
    { key: "nigeria", slug: "nigeria", label: nameOf("nigeria"), dx: 12, dy: 14 },
  ];
  const soft = ["libya", "egypt", "ivory-coast", "ghana", "kenya", "ethiopia", "tanzania", "iraq", "saudi", "uae", "azerbaijan", "uzbekistan", "kazakhstan"];
  const konya = markers["turkiye-konya"];
  const w = dotmap.width as number;
  const h = dotmap.height as number;
  const dotPath = useMemo(() => {
    return (dotmap.dots as [number, number][])
      .map(([x, y]) => `M${x},${y}m-2.6,0a2.6,2.6 0 1,0 5.2,0a2.6,2.6 0 1,0 -5.2,0`)
      .join(" ");
  }, []);
  return (
    <section className="im-section" id="markets">
      <div className="im-wrap im-map-grid">
        <div className="im-dotmap">
          <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label={t.a11y.marketsMap}>
            <path className="m-dot" d={dotPath} />
            {active.map((m) => {
              const [x, y] = markers[m.key];
              return <path className="m-lane" key={`lane-${m.key}`} d={`M${konya[0]} ${konya[1]} Q ${(konya[0] + x) / 2} ${Math.min(konya[1], y) - 60} ${x} ${y}`} />;
            })}
            <circle className="m-origin" cx={konya[0]} cy={konya[1]} r="4" />
            <text className="m-label" x={konya[0] + 10} y={konya[1] - 6}>
              Konya
            </text>
            {soft.map((k) => {
              const [x, y] = markers[k];
              return <circle className="m-mark--soft m-mark" key={k} cx={x} cy={y} r="3" />;
            })}
            {active.map((m) => {
              const [x, y] = markers[m.key];
              return (
                <Link key={m.key} to={href(lang, `/markets/${m.slug}`)} aria-label={m.label}>
                  <g>
                    <circle className="m-ring" cx={x} cy={y} r="6" />
                    <circle className="m-mark" cx={x} cy={y} r="4" />
                    <line className="m-leader" x1={x} y1={y} x2={x + m.dx * 0.6} y2={y + m.dy} />
                    <text className="m-label" x={x + m.dx} y={y + m.dy + 4} textAnchor={m.anchor ?? "start"}>
                      {m.label}
                    </text>
                  </g>
                </Link>
              );
            })}
          </svg>
        </div>
        <Reveal>
          <Title className="im-display">{t.marketsSection.title}</Title>
          <p className="im-body-text mt-5">{t.marketsSection.body}</p>
          <div className="mt-10">
            {t.marketsSection.priorities.map((p, i) => (
              <LedgerLink key={p.name} to={href(lang, `/markets`)} index={`0${i + 1}`} title={p.name} sub={p.countries} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- 6. Process rail ---------- */
export function ProcessRail({ lang, t }: { lang: Lang; t: Dictionary }) {
  const rail = useRef<HTMLDivElement | null>(null);
  const scrollBy = (dir: number) => rail.current?.scrollBy({ left: dir * 320, behavior: "smooth" });
  return (
    <section className="im-section" id="process">
      <div className="im-wrap">
        <Reveal className="text-center">
          <h2 className="im-display-sm">{t.process.title}</h2>
          <p className="im-body-text mx-auto mt-4">{t.process.intro}</p>
        </Reveal>
        <div className="im-rail mt-12" ref={rail}>
          {t.process.steps.map((s, i) => (
            <article className="im-rail__card" key={s.title}>
              <span className="im-rail__n">
                0{i + 1} / 08
              </span>
              <div className="im-rail__rule" />
              <h3 className="im-rail__title">{s.title}</h3>
              <p className="im-rail__body">{s.body}</p>
            </article>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="im-mono mt-4 max-w-[60ch]">{t.process.note}</p>
          <div className="im-rail__scroller">
            <button type="button" aria-label={t.a11y.prevSteps} onClick={() => scrollBy(-1)}>
              ←
            </button>
            <button type="button" aria-label={t.a11y.nextSteps} onClick={() => scrollBy(1)}>
              →
            </button>
          </div>
        </div>
        <div className="mt-6">
          <Link className="im-route-link" to={href(lang, "/how-we-work")}>
            <svg aria-hidden="true" viewBox="0 0 84 18">
              <path className="route-path" d="M0 9 C 20 9, 24 2, 40 2 S 60 16, 84 16" />
              <circle className="route-dot" r="3" />
            </svg>
            <span>{t.nav.how}</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------- 7. Network references ---------- */
export function References({ t }: { t: Dictionary }) {
  return (
    <section className="im-section" id="references">
      <div className="im-wrap">
        <Reveal>
          <p className="im-eyebrow">{t.references.eyebrow}</p>
          <h2 className="im-display-sm mt-4 max-w-[18ch]">{t.references.title}</h2>
        </Reveal>
        <div className="im-refs mt-12">
          {t.references.items.map((r) => (
            <Reveal as="article" key={r.title}>
              <div className="im-ref__frame">
                <img src={r.image} alt={r.title} width={600} height={400} loading="lazy" decoding="async"/>
                <span className="im-ref__where">{r.where}</span>
              </div>
              <h3 className="im-h3 mt-6">{r.title}</h3>
              <p className="im-acc__text mt-3">{r.body}</p>
              <ul className="im-ref__facts">
                {r.facts.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
        <p className="im-mono mt-10 max-w-[70ch]">{t.references.disclaimer}</p>
      </div>
    </section>
  );
}
